import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'

import ButtonContainer from 'components/CarSettings/Common/Creation/ButtonContainer'
import ToggleContainer from 'components/CarSettings/Common/Creation/Toggle'
import { TranslationContainer } from 'components/CarSettings/style'
import ErrorMessage from 'components/Common/ErrorMessage'
import Translation from 'components/General/Translation'
import { TranslationItemType } from 'components/General/Translation/TranslationListItem'

import { COUNTRY_VERSION_CREATION_OPTION } from 'constants/countryVersion'
import { textFiles } from 'constants/textFiles'
import useTranslation from 'hooks/useTranslation'
import { CountryVersion } from 'models/car'
import { PublicationStatus } from 'models/status'
import { TranslationChange } from 'models/translations'
import { sleep } from 'utils/BasicUtil'
import { CountryVersionCreationProps } from 'utils/CarSettings/CountryVersion/creation'

import {
  StyledContainer,
  StyledForm,
  StyledTextField,
  StyledTextFieldContainer,
} from 'styles/creation'

const CountryVersionInfo = ({
  handleContinue,
  countryVersionData,
  updateCountryVersionData,
}: CountryVersionCreationProps) => {
  const { countryVersionInformation } = countryVersionData
  const [countryVersionStatus, setCountryVersionStatus] = useState<boolean>(
    countryVersionInformation.status === PublicationStatus.PUBLISHED
  )
  const [stepInfo] = useState<CountryVersion>(countryVersionInformation)
  const [addedTranslations, setAddedTranslations] = useState<
    TranslationItemType[]
  >(countryVersionInformation.translations)

  const { text: translation } = useTranslation(
    textFiles.COUNTRY_VERSION_CREATION
  )
  const { information } = translation

  const { text: validationText } = useTranslation(textFiles.VALIDATION)

  const handleChangeTranslation = (
    translations: TranslationItemType[],
    type: TranslationChange
  ) => {
    sessionStorage.setItem('type', type)
    setAddedTranslations(translations)
  }

  const validationSchema = yup.object({
    country: yup.string().required(validationText.fieldRequired),
  })

  const formik = useFormik<Partial<CountryVersion>>({
    initialValues: {
      country: countryVersionInformation.country,
    },
    validationSchema,
    onSubmit: async (values) => {
      await sleep(1000)
      updateCountryVersionData({
        type: COUNTRY_VERSION_CREATION_OPTION.UPDATE_COUNTRY_VERSION_INFORMATION,
        payload: {
          ...stepInfo,
          country: values.country as string,
          translations: addedTranslations,
          status: countryVersionStatus
            ? PublicationStatus.PUBLISHED
            : PublicationStatus.UNPUBLISHED,
        },
      })

      handleContinue()
    },
  })

  return (
    <StyledContainer>
      <StyledForm onSubmit={formik.handleSubmit}>
        <div>
          <ToggleContainer
            title={information.isPublished}
            description={information.isPublishedDescription}
            status={countryVersionStatus}
            handleStatus={setCountryVersionStatus}
            testId="published"
          />
          <StyledTextFieldContainer
            title={information.country}
            description={information.countryDescription}
          >
            <StyledTextField
              variant="outlined"
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
              placeholder="USA"
              error={formik.touched.country && Boolean(formik.errors.country)}
            />
            {formik.touched.country && formik.errors.country ? (
              <ErrorMessage
                sx={{
                  alignSelf: 'flex-start',
                  position: 'static',
                  marginTop: '0.5rem',
                }}
                text={formik.errors.country}
              />
            ) : null}
          </StyledTextFieldContainer>
          <TranslationContainer>
            <Translation
              addedTranslations={addedTranslations}
              handleChangeTranslation={handleChangeTranslation}
              translationDescription={information.translationsDescription}
            />
          </TranslationContainer>
        </div>
      </StyledForm>
      <ButtonContainer
        isFirstStep
        confirmButtonType="submit"
        nextFunction={formik.handleSubmit}
      />
    </StyledContainer>
  )
}

export default CountryVersionInfo
